import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getColorfulGameBGByIndex } from '../../../../constants/Games/gameBackground';
import { SVGIconType } from '../../../../constants/svgIcon';
import { Locale } from '../../../../models/Base';
import { EvaluationType } from '../../../../models/GameControllerStore';
import {
  InAppEvaluation,
  TextEvaluationWord,
} from '../../../../models/RecordStore';
import { ProfileExample } from '../../../../models/UserStore';
import {
  InGameSoundEffects,
  useGameControllerStore,
} from '../../../../stores/GameControllerStore';
import { useSessionStore } from '../../../../stores/SessionStore';
import { useUserStore } from '../../../../stores/UserStore';
import {
  AchievementInfoCell,
  BreadBar,
  GameActionButton,
  GameActionButtonVariant,
  LoadingSpinner,
  SvgIcon,
} from '../../../Molecules';
import {
  AnalysisTextEvaluationPanel,
  GameEndVSResultGroup,
  GameEndVSResultGroupProps,
} from '../../../Organisms';

import styles from './GameEndTemplate.module.css'; // Import CSS Module

export enum GameEndButtonText {
  NextStation = 'Next Station',
  PlayImprovementGame = 'Play Improvement Practice',
  PlayFullGame = 'Play Full Practice',
  Exit = 'Exit',
  NextPractice = 'Next Practice',
  Goal = 'Goal',
}

type GameEndTemplateProps = {
  gameBGIndex: number | undefined;
  gameEndVSResultGroupProps: GameEndVSResultGroupProps;
  isChallenge: boolean;
  analysisEvaluation?: InAppEvaluation<TextEvaluationWord>;
  answerLocale: Locale;
  buttonText?: GameEndButtonText;
  buttonProgress:
    | { currentProgress: number; totalProgress: number }
    | undefined;
  evaluationKey: EvaluationType;
  rewardEXP: number;
  rewardBread: number;
  isUseTranslate: boolean;
  originalLanguage: string;
  translatedLanguage: string;
  originalAudioTts: string | null | undefined;
  translatedAudioTts: string | null | undefined;
  translationResult: string | undefined;
  isHandwriting: boolean;
  isLoading: boolean;
  isDisableRewardAnimation: boolean;
  onClickNext: () => void;
  onClickExit: () => void;
  onSwitchTranslate: () => void;
  onClickLanguage: () => void;
  onClickAnalysis: () => void;
  onClickShare?: () => void;
};

const { getCurrentUser } = useUserStore.getState();

const GameEndTemplate = memo(
  ({
    gameBGIndex,
    gameEndVSResultGroupProps,
    isChallenge,
    analysisEvaluation,
    evaluationKey,
    answerLocale,
    buttonText = GameEndButtonText.NextStation,
    buttonProgress,
    rewardEXP,
    rewardBread,
    isUseTranslate,
    originalLanguage,
    translatedLanguage,
    originalAudioTts,
    translatedAudioTts,
    translationResult,
    isHandwriting,
    isLoading,
    isDisableRewardAnimation,
    onClickNext,
    onClickExit,
    // onSwitchTranslate,
    // onClickLanguage,
    onClickAnalysis,
    onClickShare,
  }: GameEndTemplateProps) => {
    const { t } = useTranslation();
    const userId = useSessionStore((state) => state.activeUserId);

    const gameBG =
      gameBGIndex !== undefined
        ? getColorfulGameBGByIndex(gameBGIndex)
        : undefined;
    const [step, setStep] = useState<'VS' | 'Analysis'>('VS');
    // const [expBefore, setExpBefore] = useState<number>(0);
    // const [breadBefore, setBreadBefore] = useState<number>(0);
    const [expDiff, setExpDiff] = useState(0);
    const [breadDiff, setBreadDiff] = useState(0);
    const [displayedEXP, setDisplayedEXP] = useState(0);
    const [displayedBread, setDisplayedBread] = useState(0);
    const [isPlayingAnimation, setIsPlayingAnimation] = useState(false);
    const [isBrainIcon, setIsBrainIcon] = useState(
      rewardEXP > 0 ? true : false,
    );

    const [isReadyToDisplay, setIsReadyToDisplay] = useState(false);
    // const [displayText, setDisplayText] = useState('');

    // useEffect(() => {
    //   setDisplayText(Math.random() < 0.5 ? t('Share') : t('Ask Your Friend'));
    // }, [t]);

    const nextStep = () => {
      // Switch step between "VS" and "Analysis" when called
      setStep((prevStep) => (prevStep === 'VS' ? 'Analysis' : 'VS'));
    };

    // useEffect(() => {
    //   // if (isDisableRewardAnimation) {
    //   //   return;
    //   // }
    //   const initialExp = gameEndVSResultGroupProps.currentUserProfile.exp.total;
    //   const initialBread = getAccount(userId ?? '')?.balance.breadSlice ?? 0;

    //   // console.log('initialExp', initialExp);
    //   // console.log('initialBread', initialBread);

    //   setExpBefore(initialExp);
    //   setBreadBefore(initialBread);
    // }, [
    //   gameEndVSResultGroupProps.currentUserProfile.exp.total,
    //   isDisableRewardAnimation,
    //   userId,
    // ]);

    useEffect(() => {
      setTimeout(() => {
        getCurrentUser().catch(console.error);
      }, 3000);
    }, []);
    const breadSlice = Math.max(
      useUserStore((userState) => {
        const account = userState.getAccount(userId ?? '');
        return account?.balance?.breadSlice ?? 0;
      }),
      0,
    );
    const profile = useUserStore(
      (userState) => userState.getProfile(userId ?? '') ?? ProfileExample,
    );
    const exp = Math.max(profile?.exp.total, 0);

    useEffect(() => {
      if (rewardEXP !== 0 && rewardEXP !== -1 && !isDisableRewardAnimation) {
        setTimeout(() => {
          // console.log('expDiff', rewardEXP);
          setExpDiff(rewardEXP);
        }, 1500);
      }
      if (
        rewardBread !== 0 &&
        rewardBread !== -1 &&
        !isDisableRewardAnimation
      ) {
        setTimeout(() => {
          // console.log('breadDiff', rewardBread);
          setBreadDiff(rewardBread);
        }, 1500);
      }
    }, [isDisableRewardAnimation, rewardBread, rewardEXP]);

    useEffect(() => {
      if (displayedEXP < exp && displayedEXP === 0) {
        setDisplayedEXP(exp - rewardEXP); // minus the rewardexp by current exp to let user see exp increase
        setDisplayedBread(breadSlice - rewardBread);
        setIsReadyToDisplay(true);
        return;
      }
      // if (
      //   breadBefore !== breadSlice &&
      //   expBefore === exp &&
      //   !isDisableRewardAnimation &&
      //   displayedEXP < expBefore
      // ) {
      //   // it seems that server won't update the profile if request for profile in a short period of time,
      //   // so added this to update displayed exp locally.
      //   setDisplayedEXP(expBefore);
      // }
    }, [
      // breadBefore,
      breadSlice,
      displayedEXP,
      exp,
      // expBefore,
      isDisableRewardAnimation,
      isPlayingAnimation,
      rewardBread,
      rewardEXP,
    ]);

    const resultAnalysisEvaluation:
      | InAppEvaluation<TextEvaluationWord>
      | undefined = analysisEvaluation
      ? {
          ...analysisEvaluation,
          inApp: analysisEvaluation?.inApp.map(
            (e) =>
              new TextEvaluationWord({ ...e, records: e.records.slice(-1) }),
          ),
          improvement: analysisEvaluation?.improvement?.map(
            (e) =>
              new TextEvaluationWord({ ...e, records: e.records.slice(-1) }),
          ),
        }
      : undefined;

    const handleExpAnimationEnd = useCallback(() => {
      setIsPlayingAnimation(false);
      setExpDiff(-1);
      setIsBrainIcon(false);
    }, []);

    const handleAnimationStart = useCallback(() => {
      setIsPlayingAnimation(true);
      // FIXME: handle the sound effect outside or pass in the game controller instead?
      useGameControllerStore
        .getState()
        .audioPlayer.playAudio(InGameSoundEffects.earnBread, true);
    }, []);

    const handleBreadAnimationEnd = () => {
      setBreadDiff(-1);
    };

    const handleAddExp = useCallback(() => {
      const expIncrement = rewardEXP;
      const breadIncrement = rewardBread;

      // Handle EXP counting
      for (let i = 1; i <= expIncrement; i++) {
        setTimeout(() => {
          setDisplayedEXP((prev) => prev + 1);
        }, i * 40);
      }

      // Handle bread counting
      for (let i = 1; i <= breadIncrement; i++) {
        setTimeout(() => {
          setDisplayedBread((prev) => prev + 1);
        }, i * 40);
      }
    }, [rewardEXP, rewardBread]);

    const addExpAnimationProps = useMemo(
      () => ({
        value: expDiff,
        style: `${styles.xpBarPos} ${styles.green}`,
        onAnimationStart: handleAnimationStart,
        onAnimationEnd: handleExpAnimationEnd,
        addEXP: handleAddExp,
      }),
      [expDiff, handleAnimationStart, handleExpAnimationEnd, handleAddExp],
    );

    return (
      <div
        className={styles.backgroundImage}
        style={
          gameBG
            ? {
                backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)),
                url(${gameBG})`,
              }
            : undefined
        }
      >
        <div className={styles.gameEndTemplate}>
          {step === 'Analysis' && (
            <div className={styles.shareContainer} onClick={onClickShare}>
              <SvgIcon name={SVGIconType.Share} width={28} height={28} />
              <p
                style={{
                  filter: 'drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.5))',
                }}
              >
                {t('Share')}
              </p>
            </div>
          )}
          {
            // space holder for share button
          }
          {step === 'Analysis' && (
            <div style={{ width: '100%', height: '50px' }} />
          )}
          {step === 'VS' && (
            <div className={styles.stepContainer}>
              <AnimatedNumber
                value={expDiff}
                style={styles.xpBarPos}
                isShowNumber={false}
                isShowOverlay={true}
              />
              <div
                className={`${styles.xpBarContainer} ${styles.excludeOverlay}`}
              >
                {isReadyToDisplay ? (
                  <AchievementInfoCell
                    userProfile={profile}
                    level={profile.exp.level}
                    progress={profile.exp.progress}
                    currentExp={displayedEXP}
                    totalExp={profile.exp.nextLevelRequirement}
                    socialExp={profile.exp.socialContribution.total}
                    knowledgeExp={profile.exp.knowledge.total}
                    addExpAnimation={addExpAnimationProps}
                    isBrainIcon={isBrainIcon}
                    isPlayingAnimation={isPlayingAnimation}
                  />
                ) : (
                  <AchievementInfoCell // for placeholding without affecting animation
                    userProfile={profile}
                    level={profile.exp.level}
                    progress={profile.exp.progress}
                    currentExp={profile.exp.total - rewardEXP}
                    totalExp={profile.exp.nextLevelRequirement}
                    socialExp={profile.exp.socialContribution.total}
                    knowledgeExp={profile.exp.knowledge.total}
                    addExpAnimation={addExpAnimationProps}
                    isBrainIcon={isBrainIcon}
                  />
                )}
              </div>
              <div
                className={`${styles.assetBarContainer} ${styles.excludeOverlay}`}
              >
                <AnimatedNumber
                  value={breadDiff}
                  onAnimationEnd={handleBreadAnimationEnd}
                  style={`${styles.assetBarPos} ${styles.orange}`}
                />
                <div className={styles.breadBarContainer}>
                  <p
                    style={{
                      fontWeight: 'bold',
                      color: '#99521F',
                      fontSize: '1.2rem',
                    }}
                  >
                    {t('Bread')}
                  </p>
                  {isReadyToDisplay ? (
                    <BreadBar
                      breadSlice={displayedBread}
                      height="20px"
                      width="160px"
                      iconSize={34}
                      backgroundColor="#fff"
                      isAnimating={isPlayingAnimation}
                      breadPosRatio={4}
                      levelFontsizeRatio={2.125}
                    />
                  ) : (
                    <BreadBar
                      breadSlice={breadSlice - rewardBread}
                      height="20px"
                      width="160px"
                      iconSize={34}
                      backgroundColor="#fff"
                      isAnimating={isPlayingAnimation}
                      breadPosRatio={4}
                      levelFontsizeRatio={2.125}
                    />
                  )}
                </div>
              </div>
              <GameEndVSResultGroup {...gameEndVSResultGroupProps} />

              {isChallenge ? (
                <NextButtonContainer
                  buttonText={buttonText}
                  buttonProgress={buttonProgress}
                  onClickNext={onClickNext}
                  onClickShare={onClickShare}
                />
              ) : (
                <div className={styles.nextStepButton} onClick={nextStep}>
                  <SvgIcon
                    name={SVGIconType.Arrow}
                    width={28}
                    height={28}
                    color="white"
                  />
                </div>
              )}

              {isChallenge ? (
                <div className={styles.exitButton} onClick={onClickExit}>
                  <SvgIcon
                    name={SVGIconType.Logout}
                    width={24}
                    height={24}
                    color="#3BB9AE"
                  />
                  <p className={styles.exitButtonLabel}>{t('Exit')}</p>
                </div>
              ) : null}
            </div>
          )}

          {step === 'Analysis' && (
            <>
              <div className={styles.stepContainer}>
                {resultAnalysisEvaluation ? (
                  <AnalysisTextEvaluationPanel
                    recordScore={gameEndVSResultGroupProps.currentUserScore}
                    recordDuration={
                      gameEndVSResultGroupProps.currentUserDuration
                    }
                    evaluation={resultAnalysisEvaluation}
                    evaluationKey={evaluationKey}
                    answerLocale={answerLocale}
                    isUseTranslate={isUseTranslate}
                    originalLanguage={originalLanguage}
                    translatedLanguage={translatedLanguage}
                    originalAudioTts={originalAudioTts}
                    translatedAudioTts={translatedAudioTts}
                    translationResult={translationResult}
                    isGameEnd={true}
                    isHandwriting={isHandwriting}
                    onSwitchTranslate={() => {}}
                    onClickLanguage={() => {}}
                    onClickAnalysis={onClickAnalysis}
                  />
                ) : null}
                <NextButtonContainer
                  buttonText={buttonText}
                  buttonProgress={buttonProgress}
                  onClickNext={onClickNext}
                  onClickShare={onClickShare}
                />

                <div className={styles.exitButton} onClick={onClickExit}>
                  <SvgIcon
                    name={SVGIconType.Logout}
                    width={24}
                    height={24}
                    color="#3BB9AE"
                  />
                  <p className={styles.exitButtonLabel}>{t('Exit')}</p>
                </div>
              </div>
              {isLoading ? <LoadingSpinner loading={isLoading} /> : null}
            </>
          )}
        </div>
      </div>
    );
  },
);

export default GameEndTemplate;

type NextButtonContainerProps = {
  buttonText: GameEndButtonText;
  buttonProgress:
    | { currentProgress: number; totalProgress: number }
    | undefined;
  onClickNext: () => void;
  onClickShare?: () => void;
};

const NextButtonContainer = memo(
  ({
    buttonText,
    buttonProgress,
    onClickNext,
    onClickShare,
  }: NextButtonContainerProps) => {
    const { t } = useTranslation();

    return (
      <div className={styles.nextButtonDiv}>
        <GameActionButton
          onClick={onClickNext}
          // leading={
          //   <SvgIcon
          //     name={
          //       buttonText === GameEndButtonText.Exit
          //         ? SVGIconType.Logout
          //         : buttonText === GameEndButtonText.NextPractice
          //         ? SVGIconType.PracticeTick
          //         : buttonText === GameEndButtonText.PlayFullGame ||
          //           buttonText === GameEndButtonText.PlayImprovementGame
          //         ? SVGIconType.Retry
          //         : SVGIconType.JoyStation
          //     }
          //     width={24}
          //     height={24}
          //     color="white"
          //   />
          // }
          trailing={
            <>
              {buttonProgress ? (
                <div className={styles.whiteBubble}>
                  {`${buttonProgress.currentProgress}/${buttonProgress.totalProgress}`}
                </div>
              ) : null}
              <SvgIcon
                name={SVGIconType.Arrow}
                width={18}
                height={18}
                color="white"
              />
            </>
          }
          variant={
            buttonText === GameEndButtonText.Exit
              ? GameActionButtonVariant.Blue
              : buttonText === GameEndButtonText.PlayFullGame ||
                buttonText === GameEndButtonText.PlayImprovementGame
              ? GameActionButtonVariant.Orange
              : GameActionButtonVariant.Green
          }
        >
          <>
            <SvgIcon
              name={
                buttonText === GameEndButtonText.Exit
                  ? SVGIconType.Logout
                  : buttonText === GameEndButtonText.NextPractice
                  ? SVGIconType.PracticeTick
                  : buttonText === GameEndButtonText.PlayFullGame ||
                    buttonText === GameEndButtonText.PlayImprovementGame
                  ? SVGIconType.Retry
                  : SVGIconType.JoyStation
              }
              width={24}
              height={24}
              color="white"
            />
            <p style={{ padding: '0 0.3rem' }}>{t(buttonText)}</p>
          </>
        </GameActionButton>
        {onClickShare && (
          <GameActionButton
            onClick={onClickShare}
            trailing={' '}
            variant={GameActionButtonVariant.LogoOrange}
          >
            <>
              <SvgIcon
                name={SVGIconType.Share}
                width={24}
                height={24}
                color="white"
              />
              <p style={{ padding: '0 0.3rem' }}>{t('Ask Your Friend')}</p>
            </>
          </GameActionButton>
        )}
      </div>
    );
  },
);

export interface AnimatedNumberProps {
  value: number;
  style: string | undefined; //isPlayingAnimation
  onAnimationStart?: () => void;
  onAnimationEnd?: () => void;
  addEXP?: () => void;
  isShowOverlay?: boolean;
  isShowNumber?: boolean;
  plusOrMinus?: boolean;
}

export const AnimatedNumber = memo(
  ({
    value,
    style,
    onAnimationStart,
    onAnimationEnd,
    addEXP,
    isShowOverlay,
    isShowNumber = true,
    plusOrMinus = true,
  }: AnimatedNumberProps) => {
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [showOverlay, setShowOverlay] = useState<boolean>(false);

    useEffect(() => {
      if (value > 0) {
        onAnimationStart?.();
        setIsVisible(true);
        setShowOverlay(true);

        const addTimer = setTimeout(() => {
          addEXP?.();
        }, 1000);

        const timer = setTimeout(() => {
          setTimeout(() => {
            setIsVisible(false);
            setShowOverlay(false);
            onAnimationEnd?.();
          }, 1150);
        }, 1500);

        return () => {
          clearTimeout(addTimer);
          clearTimeout(timer);
        };
      }
      return () => {};
    }, [value, addEXP, onAnimationEnd, onAnimationStart]);

    if (!isVisible || value === 0) return null;

    return (
      <>
        {isShowOverlay && showOverlay && <div className={styles.overlay} />}
        {isShowNumber && isVisible && value !== 0 && (
          <div className={`${styles.animatedNumber} ${style}`}>
            {plusOrMinus ? '+' : '-'}
            {value}
          </div>
        )}
      </>
    );
  },
);
